import { cn, useBreakpoints } from "@aurem/ui";
import {
  Banknote,
  ClipboardCheckIcon,
  DatabaseIcon,
  Files,
  LockKeyholeOpen,
  MailIcon,
  Package,
  Palette,
  Play,
  ShieldCheck,
  ShieldIcon,
  TextCursorInput,
  TextQuote,
  TextSelectIcon,
  WalletMinimal,
  Wrench,
} from "lucide-react";
import { useFeature } from "providers/FeaturesProvider";
import { useI18n } from "providers/I18nProvider";
import { useCallback } from "react";
import ROUTES from "../../../const/routes";
import { useUserPermissions } from "../../../graphql/user";
import { useNavigation } from "../../../hooks/navigate";
import { PermissionEnum } from "../../../types";
import {
  CONTENT_MANAGEMENT_PERMISSIONS,
  INTERNAL_PERMISSIONS,
  LOOK_AND_FEEL_PERMISSIONS,
  SETUP_PERMISSIONS,
} from "./constants";
import { ControlItem } from "./ControlItem";
import { ControlsSection } from "./ControlsSection";

type Props = {
  onClose: () => void;
};

export const ControlsContent = ({ onClose }: Props) => {
  const { t } = useI18n();
  const { isMobile } = useBreakpoints();
  const navigate = useNavigation();
  const { userPermissions, permissionsContain } = useUserPermissions();
  const isTasksEnabled = useFeature("tasks_feature");

  const handleClick = useCallback(
    (to: string) => {
      return () => {
        navigate(to);
        onClose();
      };
    },
    [navigate, onClose],
  );

  return (
    <div
      className={cn(
        isMobile
          ? "flex flex-col w-full overflow-auto h-[100dvh]"
          : "grid grid-cols-4 gap-2 w-full",
      )}
    >
      {permissionsContain(userPermissions, SETUP_PERMISSIONS) && (
        <ControlsSection title={t("apps.admin.navBar.subNavBar.setup")}>
          {permissionsContain(userPermissions, [
            PermissionEnum.ManageProduct,
          ]) && (
            <>
              <ControlItem
                icon={<Package size="1rem" />}
                label={t("apps.admin.navBar.subNavBar.featuresAndProducts")}
                onClick={handleClick(ROUTES.CONTROL_PRODUCTS)}
              />
              <ControlItem
                icon={<Play size="1rem" />}
                label={t("apps.admin.navBar.subNavBar.automations")}
                onClick={handleClick(ROUTES.CONTROL_AUTOMATIONS)}
              />
            </>
          )}
          {permissionsContain(userPermissions, [
            PermissionEnum.ManagePasswordPolicy,
          ]) && (
            <ControlItem
              icon={<LockKeyholeOpen size="1rem" />}
              label={t("apps.admin.navBar.subNavBar.accessAndRestrictions")}
              onClick={handleClick(ROUTES.CONTROL_ACCESS_AND_RESTRICTIONS)}
            />
          )}
          {permissionsContain(userPermissions, [PermissionEnum.ListRole]) && (
            <ControlItem
              icon={<ShieldIcon size="1rem" />}
              label={t("apps.admin.navBar.subNavBar.roles")}
              onClick={handleClick(ROUTES.CONTROL_ROLES)}
            />
          )}
          {isTasksEnabled &&
            permissionsContain(userPermissions, [
              PermissionEnum.ManageTasks,
            ]) && (
              <ControlItem
                icon={<ClipboardCheckIcon size="1rem" />}
                label={t("apps.admin.navBar.subNavBar.tasks.page.title")}
                onClick={handleClick(ROUTES.CONTROL_TASKS)}
              />
            )}
          {permissionsContain(userPermissions, [
            PermissionEnum.ManageKybTask,
          ]) && (
            <ControlItem
              icon={<ShieldCheck size="1rem" />}
              label={t("apps.admin.navBar.subNavBar.compliance")}
              onClick={handleClick(ROUTES.CONTROL_COMPLIANCE)}
            />
          )}
          {permissionsContain(userPermissions, [PermissionEnum.ManageFees]) && (
            <ControlItem
              icon={<Banknote size="1rem" />}
              label={t("apps.admin.navBar.subNavBar.fees")}
              onClick={handleClick(ROUTES.CONTROL_FEES)}
            />
          )}
          {permissionsContain(userPermissions, [
            PermissionEnum.ManagePaymentCategories,
          ]) && (
            <ControlItem
              icon={<WalletMinimal size="1rem" />}
              label={t("apps.admin.navBar.subNavBar.payments")}
              onClick={handleClick(ROUTES.CONTROL_PAYMENT_CATEGORIES)}
            />
          )}
        </ControlsSection>
      )}

      {permissionsContain(userPermissions, LOOK_AND_FEEL_PERMISSIONS) && (
        <ControlsSection title={t("apps.admin.navBar.subNavBar.lookAndFeel")}>
          {permissionsContain(userPermissions, [
            PermissionEnum.ManageBranding,
          ]) && (
            <ControlItem
              icon={<Palette size="1rem" />}
              label={t("apps.admin.navBar.subNavBar.branding")}
              onClick={handleClick(ROUTES.CONTROL_BRANDING)}
            />
          )}
          {permissionsContain(userPermissions, [
            PermissionEnum.ManageTranslation,
          ]) && (
            <>
              <ControlItem
                icon={<TextCursorInput size="1rem" />}
                label={t("apps.admin.navBar.subNavBar.textEditor")}
                onClick={handleClick(ROUTES.CONTROL_TRANSLATIONS)}
              />
              <ControlItem
                icon={<MailIcon size="1rem" />}
                label={t("apps.admin.navBar.subNavBar.emails")}
                onClick={handleClick(ROUTES.CONTROL_EMAILS)}
              />
            </>
          )}
        </ControlsSection>
      )}

      {permissionsContain(userPermissions, CONTENT_MANAGEMENT_PERMISSIONS) && (
        <ControlsSection
          title={t("apps.admin.navBar.subNavBar.contentManagement")}
        >
          {permissionsContain(userPermissions, [
            PermissionEnum.ManageDocument,
          ]) && (
            <ControlItem
              icon={<Files size="1rem" />}
              label={t("apps.admin.navBar.subNavBar.documents")}
              onClick={handleClick(ROUTES.CONTROL_DOCUMENTS)}
            />
          )}
          {permissionsContain(userPermissions, [
            PermissionEnum.ManageForms,
          ]) && (
            <ControlItem
              icon={<TextQuote size="1rem" />}
              label={t("apps.admin.navBar.forms")}
              onClick={handleClick(ROUTES.CONTROL_FORMS)}
            />
          )}
          {permissionsContain(userPermissions, [
            PermissionEnum.ManageForms,
          ]) && (
            <ControlItem
              icon={<TextSelectIcon size="1rem" />}
              label={t("apps.admin.navBar.subNavBar.customFields")}
              onClick={handleClick(ROUTES.CONTROL_CUSTOM_ATTRIBUTES)}
            />
          )}
        </ControlsSection>
      )}

      {permissionsContain(userPermissions, INTERNAL_PERMISSIONS) && (
        <ControlsSection title={t("apps.admin.navBar.subNavBar.internal")}>
          {permissionsContain(userPermissions, [
            PermissionEnum.ManageInternal,
          ]) && (
            <ControlItem
              icon={<DatabaseIcon size="1rem" />}
              label={t("apps.admin.navBar.subNavBar.data")}
              onClick={handleClick(ROUTES.INTERNAL_DATA)}
            />
          )}
          {permissionsContain(userPermissions, [
            PermissionEnum.ManageSupportTools,
          ]) && (
            <ControlItem
              icon={<Wrench size="1rem" />}
              label={t("apps.admin.navBar.subNavBar.supportTools")}
              onClick={handleClick(ROUTES.CONTROL_SUPPORT_TOOLS)}
            />
          )}
        </ControlsSection>
      )}
    </div>
  );
};
